import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import Chevron from 'components/Chevron';
import ResponsiveImage from 'components/ResponsiveImage';
import { IMAGE_WIDTHS } from 'constants/images';
import Theme from 'styles/theme';
import { breakpoints, cn } from 'utils/Tailwind';

import '@splidejs/react-splide/css';

const SLIDES_PER_PAGE = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
  XL: 5,
  '2XL': 6,
};

const DYCarouselRecs = ({
  heading,
  products,
  showColorway,
  showPrice,
  trackClick,
  backgroundColor,
  dataTestId,
  pdpStorytellingEnabled,
}) => {
  const carouselRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const handleOnMove = useCallback(({ index }) => {
    setActiveSlideIndex(index);
  }, []);

  const CAROUSEL_OPTIONS = useMemo(() => {
    return {
      type: 'slide',
      pagination: false,
      gap: '0.5rem',
      mediaQuery: 'min',
      breakpoints: {
        0: {
          perPage: SLIDES_PER_PAGE.XS,
          padding: {
            left: '1.5rem',
            right: '30vw',
          },
        },
        [breakpoints.sm]: {
          perPage: SLIDES_PER_PAGE.SM,
          padding: {
            left: '1.5rem',
            right: '1.5rem',
          },
        },
        [breakpoints.md]: {
          perPage: SLIDES_PER_PAGE.MD,
          padding: {
            left: pdpStorytellingEnabled ? '2.5rem' : '1.5rem',
            right: pdpStorytellingEnabled ? '2.5rem' : '1.5rem',
          },
        },
        [breakpoints.lg]: {
          perPage: SLIDES_PER_PAGE.LG,
          padding: {
            left: pdpStorytellingEnabled ? '2.5rem' : '0',
            right: '2.5rem',
          },
        },
        [breakpoints['3xl']]: {
          perPage: SLIDES_PER_PAGE['2XL'],
          padding: {
            left: pdpStorytellingEnabled ? '2.5rem' : '0',
            right: '2.5rem',
          },
        },
      },
    };
  }, [pdpStorytellingEnabled]);

  if (!heading || !products?.length) return null;

  return (
    <div
      className={cn('DYCarouselRecs', {
        'py-10': backgroundColor,
        'w-full overflow-x-hidden lg:mx-auto lg:max-w-screen-2xl lg:overflow-x-visible lg:px-6':
          !pdpStorytellingEnabled,
      })}
      style={{ backgroundColor }}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <h2
        className={cn('mb-4 px-6 font-bold uppercase tracking-wide md:mb-8', {
          'md:px-10': pdpStorytellingEnabled,
          'lg:px-0': !pdpStorytellingEnabled,
        })}
      >
        {heading}
      </h2>
      <Splide
        data-testid="splide"
        hasTrack={false}
        options={CAROUSEL_OPTIONS}
        ref={carouselRef}
        onMove={handleOnMove}
      >
        <CarouselArrows
          activeSlideIndex={activeSlideIndex}
          items={products}
          pdpStorytellingEnabled={pdpStorytellingEnabled}
        />
        <SplideTrack className="overflow-visible">
          <ColorwayCards
            items={products}
            trackClick={trackClick}
            showColorway={showColorway}
            showPrice={showPrice}
          />
        </SplideTrack>
      </Splide>
    </div>
  );
};

const CarouselArrows = ({ activeSlideIndex, items, pdpStorytellingEnabled }) => {
  return (
    <div className="splide__arrows hidden md:block">
      <button
        className={cn(`splide__arrow splide__arrow--prev !left-4 bg-white`, {
          '!hidden': !activeSlideIndex,
          '2xl:!-left-6': !pdpStorytellingEnabled,
        })}
      >
        <Chevron direction="left" size={9} />
      </button>
      <button
        className={cn(`splide__arrow splide__arrow--next !right-4 bg-white`, {
          '!hidden': activeSlideIndex >= items.length - SLIDES_PER_PAGE.LG,
        })}
      >
        <Chevron direction="right" size={9} />
      </button>
    </div>
  );
};

function ColorwayCards({ items, trackClick, showColorway, showPrice }) {
  return items.map(
    ({
      productData: {
        image_url: imageLink,
        name: productName,
        url: linkUrl,
        display_color_no_sole: colorway,
        sale_price: salePrice,
        slot_id: slotId,
        price: price,
      },
    }) => {
      return (
        <SplideSlide key={`${productName} - ${colorway}`}>
          <a
            data-testid="dy-recs-card"
            className="group/card relative flex h-full flex-col overflow-hidden"
            href={linkUrl}
            onClick={() => trackClick({ slotId, productName })}
          >
            <div className="overflow-hidden">
              <ResponsiveImage
                alt={colorway}
                className="w-full bg-grey transition-transform duration-300 ease-in-out-expo md:group-hover/card:scale-105"
                src={imageLink}
                width={IMAGE_WIDTHS.W_600}
                height={IMAGE_WIDTHS.W_600}
                lazy
              />
            </div>

            <CardContent
              colorName={colorway}
              salePrice={salePrice}
              productName={productName}
              price={price}
              showColorway={showColorway}
              showPrice={showPrice}
            />
          </a>
        </SplideSlide>
      );
    }
  );
}

function CardContent({ colorName, price, salePrice, productName, showColorway, showPrice }) {
  return (
    <span className="mt-2 flex-grow gap-1 overflow-hidden text-ellipsis whitespace-nowrap md:gap-2">
      <p className="relative block w-max text-sm font-bold transition-opacity duration-200 ease-in-out xl:text-base">
        {productName}
      </p>
      {showColorway && <p className="relative text-sm xl:text-base">{colorName}</p>}
      {showPrice && (
        <p className="text-sm xl:text-base">
          {salePrice ? (
            <>
              <span className="text-discount-red">${salePrice}</span>
              <span className="ml-2 line-through">${price}</span>
            </>
          ) : (
            <span>${price}</span>
          )}
        </p>
      )}
    </span>
  );
}

DYCarouselRecs.propTypes = {
  heading: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productData: PropTypes.shape({
        image_url: PropTypes.string,
        name: PropTypes.string,
        url: PropTypes.string,
        display_color_no_sole: PropTypes.string,
        regular_price: PropTypes.string,
        sale_price: PropTypes.string,
        slot_id: PropTypes.string,
      }),
    })
  ),
  showColorway: PropTypes.bool,
  showPrice: PropTypes.bool,
  trackClick: PropTypes.func,
  backgroundColor: PropTypes.string,
  dataTestId: PropTypes.string,
  pdpStorytellingEnabled: PropTypes.bool,
};

CardContent.propTypes = {
  colorName: PropTypes.string,
  price: PropTypes.number,
  productName: PropTypes.string,
  salePrice: PropTypes.string,
  showColorway: PropTypes.bool,
  showPrice: PropTypes.bool,
};

ColorwayCards.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  showColorway: PropTypes.bool,
  showPrice: PropTypes.bool,
  trackClick: PropTypes.func,
};

CarouselArrows.propTypes = {
  activeSlideIndex: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  pdpStorytellingEnabled: PropTypes.bool,
};

DYCarouselRecs.defaultProps = {
  heading: '',
  products: [],
  showColorway: false,
  showPrice: false,
  backgroundColor: Theme.get('colors-white'),
  trackClick: () => {},
};

export default DYCarouselRecs;
